import { useEffect, useRef, useState } from "react";
import { Mic, ArrowRight, MicOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AnalysisProgress from "@/components/AnalysisProgress";

const PLACEHOLDERS = [
  "Je voudrais refaire les peintures, les sols et la salle de bains de mon appartement.",
  "Nous avons 20 logements neufs à terminer : placo, enduits, peintures, portes et sols.",
  "Je voudrais créer deux cloisons, refaire l'électricité et remettre mon commerce en état avant l'ouverture.",
];

export default function HeroPromptInput({ compact = false }) {
  const [value, setValue] = useState("");
  const [placeholder, setPlaceholder] = useState(PLACEHOLDERS[0]);
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const recRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => { i = (i + 1) % PLACEHOLDERS.length; setPlaceholder(PLACEHOLDERS[i]); }, 6000);
    return () => clearInterval(t);
  }, []);

  const startRec = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("La dictée vocale n'est pas disponible sur ce navigateur."); return; }
    const r = new SR();
    r.lang = "fr-FR"; r.interimResults = true; r.continuous = true;
    let final = value ? value + " " : "";
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t + " "; else interim += t;
      }
      setValue(final + interim);
    };
    r.onerror = () => { toast.error("Erreur de dictée vocale."); setRecording(false); };
    r.onend = () => setRecording(false);
    r.start();
    recRef.current = r;
    setRecording(true);
  };
  const stopRec = () => { recRef.current?.stop(); setRecording(false); };

  const analyze = async () => {
    if (!value.trim() || value.trim().length < 10) { toast.error("Décrivez votre projet en quelques mots."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/projects/analyze", { description: value });
      localStorage.setItem("rama_project_id", data.id);
      localStorage.setItem("rama_project_token", data.access_token);
      nav(`/estimation/${data.id}`);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.status === 503 ? "Service saturé, réessayez dans un instant." : "L'analyse a échoué. Réessayez.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="hero-prompt" className={`prompt-shell ${compact ? "" : "p-2"} relative`}>
      <AnalysisProgress active={loading} />
      <div className="p-4 md:p-6">
        <label className="font-mono-caption text-ink-soft block mb-3">Parlez-nous de votre projet intérieur</label>
        <textarea
          data-testid="hero-prompt-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          rows={compact ? 3 : 5}
          className="w-full resize-none bg-transparent outline-none text-anthracite text-base md:text-lg leading-relaxed placeholder:text-ink-soft/60 font-display"
        />
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-black/10 p-3 md:p-4 bg-ivory/60 rounded-b-rama-md">
        <button
          data-testid="hero-mic-button"
          onClick={recording ? stopRec : startRec}
          className={`relative inline-flex items-center gap-2 px-4 py-3 rounded-full border transition ${
            recording ? "bg-bordeaux-700 text-ivory border-bordeaux-700 mic-pulse" : "bg-white text-anthracite border-black/15 hover:border-bordeaux-700 hover:text-bordeaux-700"
          }`}
          aria-label="Dicter mon projet"
        >
          {recording ? <MicOff size={18} /> : <Mic size={18} />}
          <span className="text-sm font-medium hidden sm:inline">{recording ? "Arrêter" : "Parler de mon projet"}</span>
        </button>
        <button
          data-testid="analyze-project-button"
          onClick={analyze}
          disabled={loading}
          className="btn-primary disabled:opacity-60"
        >
          {loading ? "Analyse…" : "Analyser mon projet"}
          {!loading && <ArrowRight size={18} />}
        </button>
      </div>
    </div>
  );
}
